import { useEffect, useRef } from 'react';
import { checkin } from '../gameRoundApi';
import { logAuthDebug } from '../authDebugLogger';
import { withTimeout } from '../supabaseAuthSafe';

/**
 * Fires a daily check-in call once per app session.
 * The server deduplicates (only awards points once per day), but always
 * returns season_points / lifetime_points / streak data — which we need
 * to populate the PointsDisplay badge on the homepage.
 *
 * @param {string|null} userId - The signed-in user's id, or null if not signed in.
 * @param {function} onCheckinResult - Callback that receives the checkin response.
 */
export const useAutoCheckin = (userId, onCheckinResult) => {
  const calledRef = useRef(false);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);

  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!userId) {
      logAuthDebug('auto_checkin_skip_no_user');
      return;
    }
    if (calledRef.current) {
      logAuthDebug('auto_checkin_skip_already_called', { userId });
      return;
    }

    calledRef.current = true;
    retryCountRef.current = 0;

    const maxAttempts = 3;
    const runCheckin = async () => {
      retryCountRef.current += 1;
      const attempt = retryCountRef.current;
      logAuthDebug('auto_checkin_start', { userId, attempt });

      try {
        const result = await withTimeout(checkin(), 8000, 'game-round.checkin');
        if (result) {
          logAuthDebug('auto_checkin_result', {
            userId,
            attempt,
            seasonPoints: result?.seasonPoints ?? null,
            lifetimePoints: result?.lifetimePoints ?? null,
            checkinStreak: result?.checkinStreak ?? null,
          });

          if (typeof onCheckinResult === 'function') {
            onCheckinResult(result);
          }
          return;
        }

        logAuthDebug('auto_checkin_empty_result', { userId, attempt });
      } catch (error) {
        logAuthDebug('auto_checkin_error', {
          userId,
          attempt,
          message: error?.message || String(error),
        });
      }

      if (attempt >= maxAttempts) {
        logAuthDebug('auto_checkin_exhausted', { userId, maxAttempts });
        return;
      }

      const delayMs = 1200 * attempt;
      logAuthDebug('auto_checkin_retry_scheduled', { userId, attempt, delayMs });
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        runCheckin();
      }, delayMs);
    };

    runCheckin();
  }, [userId, onCheckinResult]);
};
